import { Link, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { ChevronLeft, MapPin, ShieldCheck } from "lucide-react";
import axios from "axios";
import { useCart } from "@/lib/cart";
import { useLang } from "@/lib/lang";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRIES = [
  { code: "CA", fr: "Canada", en: "Canada" },
  { code: "US", fr: "États-Unis", en: "United States" },
  { code: "FR", fr: "France", en: "France" },
  { code: "GB", fr: "Royaume-Uni", en: "United Kingdom" },
  { code: "BE", fr: "Belgique", en: "Belgium" },
  { code: "CH", fr: "Suisse", en: "Switzerland" },
  { code: "DE", fr: "Allemagne", en: "Germany" },
  { code: "ES", fr: "Espagne", en: "Spain" },
  { code: "IT", fr: "Italie", en: "Italy" },
  { code: "AU", fr: "Australie", en: "Australia" },
  { code: "MX", fr: "Mexique", en: "Mexico" },
  { code: "BR", fr: "Brésil", en: "Brazil" },
  { code: "JP", fr: "Japon", en: "Japan" },
];

export default function Checkout() {
  const nav = useNavigate();
  const { items, subtotal, clear } = useCart();
  const { lang } = useLang();
  const T = lang === "fr" ? FR : EN;
  const [shipping, setShipping] = useState({
    full_name: "", email: "", phone: "",
    line1: "", line2: "", city: "", state: "", postal_code: "", country: "CA",
  });
  const [config, setConfig] = useState(null);
  const [paypalReady, setPaypalReady] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const btnRef = useRef(null);

  const SHIP = 5.99;
  const total = subtotal + SHIP;

  useEffect(() => {
    axios.get(`${API}/store/config`).then(r => setConfig(r.data));
  }, []);

  // Load PayPal JS SDK
  useEffect(() => {
    if (!config?.paypal_client_id) return;
    const scriptId = "paypal-sdk";
    if (document.getElementById(scriptId)) {
      setPaypalReady(true);
      return;
    }
    const s = document.createElement("script");
    s.id = scriptId;
    s.src = `https://www.paypal.com/sdk/js?client-id=${config.paypal_client_id}&currency=${config.currency}&intent=capture`;
    s.onload = () => setPaypalReady(true);
    s.onerror = () => setError("PayPal SDK failed to load");
    document.body.appendChild(s);
  }, [config]);

  const isShippingValid = () => {
    const req = ["full_name", "email", "line1", "city", "postal_code", "country"];
    return req.every(k => (shipping[k] || "").trim().length > 0) &&
           /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(shipping.email);
  };

  // Render PayPal buttons
  useEffect(() => {
    if (!paypalReady || !window.paypal || !btnRef.current || items.length === 0) return;
    btnRef.current.innerHTML = "";

    window.paypal.Buttons({
      style: { layout: "vertical", color: "gold", shape: "pill", label: "paypal", height: 48 },
      onClick: (data, actions) => {
        if (!isShippingValid()) {
          toast.error(T.fillShipping);
          return actions.reject();
        }
        return actions.resolve();
      },
      createOrder: async () => {
        setCreating(true);
        setError(null);
        try {
          const payload = {
            items: items.map(i => ({ product_id: i.product_id, size: i.size, quantity: i.quantity })),
            shipping,
            lang,
          };
          const r = await axios.post(`${API}/store/orders/create`, payload);
          setCreating(false);
          return r.data.paypal_order_id;
        } catch (e) {
          setCreating(false);
          const msg = e?.response?.data?.detail || e.message || "Order creation failed";
          setError(msg);
          toast.error(msg);
          throw e;
        }
      },
      onApprove: async (data) => {
        try {
          const r = await axios.post(`${API}/store/orders/${data.orderID}/capture`);
          if (r.data.status === "PAID") {
            const orderNumber = r.data.order_number;
            clear();
            nav(`/order/${orderNumber}`);
          } else {
            toast.error(`${T.paymentStatus} ${r.data.status}`);
          }
        } catch (e) {
          toast.error(e?.response?.data?.detail || T.captureError);
        }
      },
      onError: (err) => {
        console.error("PayPal error", err);
        toast.error(T.paypalError);
      },
      onCancel: () => toast(T.cancelled),
    }).render(btnRef.current);
  }, [paypalReady, items, shipping, lang, nav, clear, T]);

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="text-center max-w-md" data-testid="checkout-empty">
          <h1 className="font-serif-mystery text-3xl mb-4">{T.emptyTitle}</h1>
          <p className="text-zinc-600 mb-6">{T.emptyBody}</p>
          <Link to="/store">
            <Button className="bg-[#8b6f3d] hover:bg-[#a1854b] text-white rounded-full h-12 px-6">{T.back}</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid="checkout-page">
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/store" className="flex items-center gap-2 text-[#8b6f3d]" data-testid="checkout-back-link">
            <ChevronLeft size={18} /> <span className="text-sm font-mono-data uppercase tracking-widest">{T.back}</span>
          </Link>
          <div className="flex items-center gap-2 text-[#8b6f3d]">
            <MapPin size={16} />
            <span className="font-mono-data text-xs uppercase tracking-widest">{T.tag}</span>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-12 py-10 grid lg:grid-cols-[1fr_400px] gap-10">
        {/* Form */}
        <div>
          <h1 className="font-serif-mystery text-4xl mb-2">{T.title}</h1>
          <p className="text-zinc-600 mb-8">{T.subtitle}</p>

          <section className="space-y-4 mb-8">
            <h2 className="font-serif-mystery text-xl text-[#8b6f3d] mb-3">{T.contactTitle}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field testId="ship-full-name" label={T.fullName} value={shipping.full_name} onChange={v => setShipping({ ...shipping, full_name: v })} required />
              <Field testId="ship-email" label={T.email} type="email" value={shipping.email} onChange={v => setShipping({ ...shipping, email: v })} required />
              <Field testId="ship-phone" label={T.phone} value={shipping.phone} onChange={v => setShipping({ ...shipping, phone: v })} />
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="font-serif-mystery text-xl text-[#8b6f3d] mb-3">{T.shippingTitle}</h2>
            <Field testId="ship-line1" label={T.line1} value={shipping.line1} onChange={v => setShipping({ ...shipping, line1: v })} required />
            <Field testId="ship-line2" label={T.line2} value={shipping.line2} onChange={v => setShipping({ ...shipping, line2: v })} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field testId="ship-city" label={T.city} value={shipping.city} onChange={v => setShipping({ ...shipping, city: v })} required />
              <Field testId="ship-state" label={T.state} value={shipping.state} onChange={v => setShipping({ ...shipping, state: v })} />
              <Field testId="ship-postal" label={T.postal} value={shipping.postal_code} onChange={v => setShipping({ ...shipping, postal_code: v })} required />
              <div>
                <label className="block font-mono-data text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">{T.country} *</label>
                <select data-testid="ship-country" value={shipping.country} onChange={e => setShipping({ ...shipping, country: e.target.value })}
                  className="w-full border border-zinc-300 rounded-lg px-3 h-11 bg-white focus:outline-none focus:border-[#8b6f3d]">
                  {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c[lang]}</option>)}
                </select>
              </div>
            </div>
          </section>
        </div>

        {/* Summary */}
        <aside className="lg:sticky lg:top-4 h-fit border border-zinc-200 rounded-2xl p-6 bg-zinc-50" data-testid="checkout-summary">
          <h3 className="font-serif-mystery text-xl mb-4">{T.summaryTitle}</h3>
          <div className="space-y-3 mb-4 max-h-64 overflow-y-auto pr-1">
            {items.map(it => (
              <div key={`${it.product_id}-${it.size}`} className="flex gap-3">
                <img src={it.image} alt="" className="w-14 h-14 object-cover rounded" />
                <div className="flex-1 text-sm">
                  <div className="font-medium leading-tight">{it.name[lang]}</div>
                  <div className="text-xs text-zinc-500">{T.size}: {it.size} · ×{it.quantity}</div>
                </div>
                <div className="text-sm font-medium">{(it.price * it.quantity).toFixed(2)}</div>
              </div>
            ))}
          </div>
          <div className="space-y-2 text-sm border-t border-zinc-200 pt-4">
            <div className="flex justify-between text-zinc-600"><span>{T.subtotal}</span><span>{subtotal.toFixed(2)} CAD</span></div>
            <div className="flex justify-between text-zinc-600"><span>{T.shippingFee}</span><span>{SHIP.toFixed(2)} CAD</span></div>
            <div className="flex justify-between text-lg font-medium pt-2 border-t border-zinc-200">
              <span>Total</span>
              <span className="text-[#8b6f3d]" data-testid="checkout-total">{total.toFixed(2)} CAD</span>
            </div>
          </div>

          <div className="mt-6">
            <div className="flex items-center gap-2 text-xs text-zinc-500 mb-3">
              <ShieldCheck size={14} className="text-green-600" />
              <span>{T.securedByPaypal}</span>
            </div>
            {!paypalReady && <p className="text-sm text-zinc-500 text-center py-4">{T.loadingPaypal}</p>}
            {creating && <p className="text-sm text-zinc-500 text-center py-2">{T.creatingOrder}</p>}
            <div ref={btnRef} data-testid="paypal-buttons-container" />
            {error && <p className="mt-3 text-sm text-red-600" data-testid="checkout-error">{error}</p>}
          </div>

          <p className="mt-4 text-[11px] text-zinc-400 leading-relaxed">
            {T.legalFine}
          </p>
        </aside>
      </main>

      <Footer />
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required = false, testId }) {
  return (
    <div>
      <label className="block font-mono-data text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">
        {label}{required && " *"}
      </label>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full border border-zinc-300 rounded-lg px-3 h-11 bg-white focus:outline-none focus:border-[#8b6f3d]"
      />
    </div>
  );
}

const FR = {
  back: "Retour",
  tag: "Paiement sécurisé",
  title: "Finaliser la commande",
  subtitle: "Renseignez vos informations de livraison puis validez avec PayPal.",
  contactTitle: "1. Contact",
  fullName: "Nom complet",
  email: "Email",
  phone: "Téléphone",
  shippingTitle: "2. Adresse de livraison",
  line1: "Adresse (ligne 1)",
  line2: "Complément (ligne 2)",
  city: "Ville",
  state: "Province / État",
  postal: "Code postal",
  country: "Pays",
  summaryTitle: "Récapitulatif",
  subtotal: "Sous-total",
  shippingFee: "Livraison",
  size: "Taille",
  securedByPaypal: "Paiement sécurisé par PayPal",
  loadingPaypal: "Chargement du bouton PayPal…",
  creatingOrder: "Création de la commande…",
  fillShipping: "Complétez d'abord vos informations de livraison.",
  paymentStatus: "Statut du paiement:",
  captureError: "Impossible de valider le paiement.",
  paypalError: "Erreur PayPal. Veuillez réessayer.",
  cancelled: "Paiement annulé.",
  emptyTitle: "Votre panier est vide",
  emptyBody: "Ajoutez des produits avant de passer commande.",
  legalFine: "En passant commande, vous acceptez nos Conditions d'utilisation. Vos données de livraison ne sont utilisées que pour traiter la commande.",
};
const EN = {
  back: "Back",
  tag: "Secure checkout",
  title: "Complete your order",
  subtitle: "Enter your shipping information and pay with PayPal.",
  contactTitle: "1. Contact",
  fullName: "Full name",
  email: "Email",
  phone: "Phone",
  shippingTitle: "2. Shipping address",
  line1: "Address line 1",
  line2: "Address line 2",
  city: "City",
  state: "State / Province",
  postal: "Postal code",
  country: "Country",
  summaryTitle: "Order summary",
  subtotal: "Subtotal",
  shippingFee: "Shipping",
  size: "Size",
  securedByPaypal: "Secured by PayPal",
  loadingPaypal: "Loading PayPal button…",
  creatingOrder: "Creating order…",
  fillShipping: "Please fill in your shipping details first.",
  paymentStatus: "Payment status:",
  captureError: "Could not confirm payment.",
  paypalError: "PayPal error. Please try again.",
  cancelled: "Payment cancelled.",
  emptyTitle: "Your cart is empty",
  emptyBody: "Add products before checking out.",
  legalFine: "By placing your order, you accept our Terms of Use. Your shipping data is used solely to fulfil the order.",
};
